import { axiosDelete, axiosGet, axiosPatch, axiosPost } from '@/api/handlers';
import { useMutation, useQuery } from '@/api/hooks';
import { useInvalidateQuery } from '@/hooks';
import { type Recursive, getRecursiveArray } from '@/utils';
import { groupBy } from 'lodash-es';
import type { z } from 'zod';
import {
  ExpenditureNatureFormSchema,
  ExpenditureNaturesModel,
  UpdateExpenditureNatureParamsSchema,
} from '../models';
import {
  type NationalExpenditureNatureModel,
  NationalExpenditureNaturesModel,
} from '../models/national-expenditure-nature';
import { expenditureNatureNameWithCode } from '../utils';

const ExpenditureNaturesModelElement = ExpenditureNaturesModel.element;
const NationalExpenditureNaturesModelElement =
  NationalExpenditureNaturesModel.element;

const getNationalExpenditureNatures = async () => {
  const res = await axiosGet('/api/national-expenditure-natures/');
  return NationalExpenditureNaturesModel.parse(res);
};
export const useNationalExpenditureNatures = <
  T = Array<z.infer<typeof NationalExpenditureNaturesModelElement>>,
>(
  select?: (data: z.infer<typeof NationalExpenditureNaturesModel>) => T,
) => {
  return useQuery({
    queryKey: ['nationalExpenditureNatures'],
    queryFn: getNationalExpenditureNatures,
    select,
  });
};

type GetExpenditureNaturesParams = {
  params?: { referenceFiscalYear?: string };
};
const getExpenditureNatures = async ({
  params,
}: GetExpenditureNaturesParams) => {
  const expenditureNatures = await axiosGet(
    '/api/expenditure-natures/',
    params,
  );
  return ExpenditureNaturesModel.parse(expenditureNatures);
};
export const useExpenditureNatures = <
  T = Array<z.infer<typeof ExpenditureNaturesModelElement>>,
>({
  params,
  select,
  enabled = true,
}: {
  select?: (data: z.infer<typeof ExpenditureNaturesModel>) => T;
  enabled?: boolean;
} & GetExpenditureNaturesParams = {}) => {
  return useQuery({
    queryKey: ['expenditureNatures', params],
    queryFn: async () => await getExpenditureNatures({ params }),
    select,
    enabled: !!enabled,
  });
};

type ExpenditureNaturesComprehensive = Array<
  z.infer<typeof NationalExpenditureNatureModel> & {
    children: Array<
      Recursive<z.infer<typeof ExpenditureNaturesModel.element>>
    > | null;
  }
>;

export const useExpenditureNaturesComprehensive = <
  T = ExpenditureNaturesComprehensive,
>({
  showCode,
  select,
  params,
  enabled,
}: {
  showCode?: boolean;
  select?: (data: ExpenditureNaturesComprehensive) => T;
  enabled?: boolean;
} & GetExpenditureNaturesParams) => {
  return useQuery({
    queryKey: ['expenditureNatures', 'Comprehensive', params],
    queryFn: async () => {
      const [nationalExpenditureNatures, expenditureNatures] =
        await Promise.all([
          getNationalExpenditureNatures(),
          getExpenditureNatures({ params }),
        ]);
      const expenditureNaturesRecursive = getRecursiveArray(
        expenditureNatures.flatMap((d) =>
          !d.upperId
            ? showCode
              ? {
                  ...d,
                  name: expenditureNatureNameWithCode({
                    code: d.code,
                    name: d.name,
                    level: d.level,
                  }),
                }
              : d
            : [],
        ),
        expenditureNatures,
      );
      const groupedExpenditureNatures = groupBy(
        expenditureNaturesRecursive,
        'nationalExpenditureNatureId',
      );

      const expenditureNaturesComprehensive = nationalExpenditureNatures.map(
        (d) => ({
          ...d,
          name: showCode
            ? expenditureNatureNameWithCode({
                code: d.code,
                name: d.name,
              })
            : d.name,
          children: groupedExpenditureNatures[d.id] ?? null,
        }),
      );
      return expenditureNaturesComprehensive;
    },
    select,
    enabled,
  });
};

type FormValues = z.infer<typeof ExpenditureNatureFormSchema>;

export const useCreateExpenditureNature = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (values: FormValues) => {
      const { period, ...payload } = ExpenditureNatureFormSchema.parse(values);
      const res = await axiosPost('/api/expenditure-natures/', {
        ...payload,
        startYear: period?.[0]?.year() ?? null,
        endYear: period?.[1]?.year() ?? null,
      });
      await invalidate(['expenditureNatures']);

      return ExpenditureNaturesModelElement.parse(res);
    },
  });
};

export const useUpdateExpenditureNature = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (values: FormValues & { id: string }) => {
      const { id, period, ...payload } =
        UpdateExpenditureNatureParamsSchema.parse(values);
      const res = await axiosPatch(`/api/expenditure-natures/${id}/`, {
        ...payload,
        startYear: period?.[0]?.year() ?? null,
        endYear: period?.[1]?.year() ?? null,
      });
      await invalidate(['expenditureNatures']);

      return ExpenditureNaturesModelElement.parse(res);
    },
  });
};

export const useDeleteExpenditureNature = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await axiosDelete(`/api/expenditure-natures/${id}/`);
      await invalidate(['expenditureNatures']);

      return res;
    },
  });
};
